import React from 'react';
import { Tag } from 'antd';
import WorkOrderTableRowWithoutActions from '../work_order_table_row_without_actions/WorkOrderTableRowWithoutActions';
import { withUserType } from '../withUserType';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { cn } from '../../utils/ClassNamesUtils';
import {
	DuplicateDecision,
	DuplicateJudgement,
	DuplicateJudgementLabels,
} from '../../types/DuplicateJudgementTypes';

// A label pill shows once Jev's answer clears this; below it the pill is hidden. The backend
// leaves the cut to the front end, so tune it here.
const LABEL_PILL_THRESHOLD = 0.7;

// Fixed display order, strongest evidence of a true duplicate first.
const LABEL_PILLS: { key: keyof DuplicateJudgementLabels; text: string }[] = [
	{ key: 'sameAsset', text: 'Same asset' },
	{ key: 'sameProblem', text: 'Same problem' },
	{ key: 'sameSymptom', text: 'Same symptom' },
	{ key: 'isFollowup', text: 'Looks like a follow-up' },
];

// One color for every verdict, so the chip's text carries the difference. The color follows the
// app's role theme: purple for buyer, teal for supplier.
const VERDICT_CHIP_CLASSES_BY_ROLE = {
	[ROLE_TYPES.BUYER]: '!border-purple-3 !bg-purple-1 !text-purple-7',
	[ROLE_TYPES.SUPPLIER]: '!border-teal-2 !bg-teal-1 !text-teal-3',
};

const VERDICT_LABELS: Partial<Record<DuplicateDecision, string>> = {
	duplicate: 'Likely duplicate',
	review: 'Possibly related',
};

// Shared by the verdict chip and the why-pills so every tag in the row is the same size.
const TAG_SIZE_CLASSES = 'bodymedium !mr-0 !rounded-md !px-s !py-2xs';

const getFiredPills = (labels: DuplicateJudgementLabels = {}) =>
	// An absent key means Jev did not answer that question, so it never shows a pill.
	LABEL_PILLS.filter(
		({ key }) => typeof labels[key] === 'number' && labels[key] >= LABEL_PILL_THRESHOLD
	);

/**
 * One candidate on the wizard's duplicate-check step: a tag row on top — Jev's verdict chip, then
 * pills saying why it matched — followed by the existing work order row. Rows Jev could not judge,
 * and rows from the asset-step check (which never goes through Jev), render as the plain row.
 */
const DuplicateWorkOrderCardComponent: React.FC<{ workOrder: any; userType: string }> = ({
	workOrder,
	userType,
}) => {
	const judgement: DuplicateJudgement = nullSafeGet('duplicateJudgement', workOrder);
	const isJudged = !!judgement && judgement.status === 'ok';
	const verdictLabel = isJudged ? VERDICT_LABELS[judgement.decision] : undefined;
	const firedPills = isJudged ? getFiredPills(judgement.labels) : [];
	const hasTagRow = !!verdictLabel || firedPills.length > 0;
	return (
		<div className="flex flex-col gap-s">
			{hasTagRow && (
				<div className="flex flex-wrap items-center gap-s">
					{verdictLabel && (
						<Tag className={cn(TAG_SIZE_CLASSES, VERDICT_CHIP_CLASSES_BY_ROLE[userType])}>
							{verdictLabel}
						</Tag>
					)}
					{firedPills.map(({ key, text }) => (
						<Tag key={key} className={TAG_SIZE_CLASSES}>
							{text}
						</Tag>
					))}
				</div>
			)}
			<WorkOrderTableRowWithoutActions record={workOrder} openInNewTab />
		</div>
	);
};

export const DuplicateWorkOrderCard = withUserType(DuplicateWorkOrderCardComponent);

export const DUPLICATE_WORK_ORDER_COLUMNS = [
	{
		title: 'Name',
		dataIndex: 'id',
		render: (text, workOrder) => <DuplicateWorkOrderCard workOrder={workOrder} />,
	},
];
